<script setup>
import { computed, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiBadgeList from '@/components/ui/UiBadgeList.vue';
import UiCard from '@/components/ui/UiCard.vue';
import UiDetailList from '@/components/ui/UiDetailList.vue';
import UiDialog from '@/components/ui/UiDialog.vue';
import UiEmptyState from '@/components/ui/UiEmptyState.vue';
import UiPageHeader from '@/components/ui/UiPageHeader.vue';
import UiPerson from '@/components/ui/UiPerson.vue';
import UiPostFeed from '@/components/ui/UiPostFeed.vue';
import UiStatCard from '@/components/ui/UiStatCard.vue';
import UiSteps from '@/components/ui/UiSteps.vue';
import PageTabs from '@/components/Utils/PageTabs.vue';
import { apiRequest } from '@/api/http';
import { toFeedPosts, useAnnouncementComments } from '@/composables/useAnnouncementComments';
import { complianceStatus, reportType } from '@/constants/reports';
import { formatDate, formatDateTime, formatTime, titleCase } from '@/utils/format';

// Parent dashboard: one child at a time (tabs when there are several) —
// attendance, LARDO alert, at-risk subjects, discipline case and teacher
// announcements.
const DISCIPLINE_STEPS = ['Reported', 'Counseled', 'Referred to the Prefect of Discipline'];
// Report status 0 → adviser, 1 → prefect, 4 → resolved (2/3 aren't used).
const STEP_OF_STATUS = { 0: 0, 1: 1, 4: 2 };
const STATUS_LABEL = { 0: 'Class adviser review', 1: 'Prefect of Discipline review', 4: 'Resolved' };

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);

const children = shallowRef([]);
const childId = shallowRef(null);
const child = computed(() => children.value.find((c) => c.id === childId.value) ?? null);
const childTabs = computed(() => children.value.map((c) => ({ id: c.id, name: c.name.split(' ')[0] })));
const loadingChildren = shallowRef(false);

async function loadChildren() {
  loadingChildren.value = true;
  try {
    const rows = await apiRequest('get', `/parent-records/getMyChildrenList/${year.value}`);
    children.value = (rows || []).map((c) => ({ ...c, name: titleCase(c.name) }));
    // Keep the selected child when the list reloads.
    if (!children.value.some((c) => c.id === childId.value)) childId.value = children.value[0]?.id ?? null;
  } catch {
    children.value = [];
    childId.value = null;
  } finally {
    loadingChildren.value = false;
  }
}
watch(year, loadChildren, { immediate: true });

// The selected child's records
const attendance = shallowRef({});
const lardo = shallowRef(null);
const atRisk = shallowRef([]);
const discipline = shallowRef(null);
const posts = shallowRef([]);
const loadingChild = shallowRef(false);

async function loadPosts() {
  if (!childId.value) return;
  posts.value = toFeedPosts(
    await apiRequest('get', `/parent-records/getParentAnnouncements/${year.value}/${childId.value}`).catch(() => []),
  );
}

async function loadChild() {
  if (!childId.value) {
    attendance.value = {};
    lardo.value = discipline.value = null;
    atRisk.value = [];
    posts.value = [];
    return;
  }
  loadingChild.value = true;
  const id = childId.value;
  const [att, alerts] = await Promise.all([
    apiRequest('get', `/parent-records/getStudentAchievements/${year.value}/${id}`).catch(() => ({})),
    apiRequest('get', `/parent-records/getStudentAlerts/${year.value}/${id}`).catch(() => ({})),
    loadPosts(),
  ]);
  if (id !== childId.value) return; // switched child meanwhile
  attendance.value = att || {};
  lardo.value = alerts?.lardoData || null;
  atRisk.value = alerts?.atRiskData || [];
  discipline.value = alerts?.disciplineData || null;
  loadingChild.value = false;
}
watch([childId, year], loadChild);

const { sending, comment, canDelete, removeComment } = useAnnouncementComments(loadPosts);

// One row per subject and term: the grade and what the school will do.
const atRiskItems = computed(() =>
  atRisk.value.map((r) => ({
    label: `${r.subject_title} · ${r.quarter}`,
    value: `${Number(r.transmuted_grade)} — ${r.remarks}`,
    span: 2,
  })),
);

const percent = computed(() => Number(attendance.value.percent) || 0);
const attendanceTone = computed(() => (percent.value >= 90 ? '#10b981' : percent.value >= 75 ? '#f59e0b' : '#ef4444'));

const tagged = computed(() => {
  const raw = discipline.value?.tag_students;
  if (!raw) return [];
  try {
    return Array.isArray(raw) ? raw : JSON.parse(raw);
  } catch {
    return [];
  }
});

const disciplineOpen = shallowRef(false);
// "Note from Juan Dela Cruz, class adviser", or just the role when the
// writer isn't known.
const noteBy = (name, role) => `Note from ${name ? `${titleCase(name)}, ${role}` : `the ${role}`}`;
</script>

<template>
  <div class="page-stack">
    <UiPageHeader title="Dashboard" subtitle="How your child is doing this school year." />

    <UiCard v-if="!loadingChildren && !children.length">
      <UiEmptyState
        icon="mdi-account-search-outline"
        title="No children linked"
        description="Add your child from My children using their LRN."
      />
    </UiCard>

    <template v-else>
      <div class="child-bar">
        <UiPerson
          v-if="child"
          :name="child.name"
          :subtitle="[child.lrnNo && `LRN ${child.lrnNo}`, child.grade_level].filter(Boolean).join(' · ')"
          :size="44"
        />
        <PageTabs v-if="children.length > 1" v-model="childId" :items="childTabs" />
      </div>

      <section class="stat-grid" aria-label="Attendance">
        <UiStatCard
          title="Attendance"
          icon="mdi-calendar-check-outline"
          :value="`${percent}%`"
          :progress="percent"
          :tone="attendanceTone"
          description="Present days out of school days"
          :loading="loadingChild"
        />
        <UiStatCard title="Present" icon="mdi-check-circle-outline" :value="attendance.present ?? 0" description="Days" :loading="loadingChild" />
        <UiStatCard title="Absent" icon="mdi-close-circle-outline" :value="attendance.absent ?? 0" description="Days" :loading="loadingChild" />
        <UiStatCard title="Excused" icon="mdi-file-document-outline" :value="attendance.excuse ?? 0" description="Days" :loading="loadingChild" />
      </section>

      <section v-if="lardo || atRisk.length || discipline" class="chart-grid">
        <UiCard v-if="lardo" title="Attendance alert" description="Your child may be at risk of dropping out (LARDO).">
          <template #actions><UiBadge variant="red">Needs attention</UiBadge></template>
          <UiDetailList
            :items="[
              { label: 'Reason', value: lardo.remarks, span: 2 },
              { label: 'Teacher', value: lardo.teacher_name },
              { label: 'Recommendation', value: lardo.recommendation },
            ]"
          />
        </UiCard>

        <UiCard v-if="atRisk.length" title="Grade alert" description="Your child is at risk of failing these subjects.">
          <template #actions><UiBadge variant="red">Needs attention</UiBadge></template>
          <UiDetailList :items="atRiskItems" />
        </UiCard>

        <UiCard v-if="discipline" title="Discipline case" :description="`${reportType(discipline.report_type).name} concern`">
          <template #actions>
            <UiBadge :variant="discipline.status === 4 ? 'green' : 'amber'">
              {{ STATUS_LABEL[discipline.status] ?? 'In review' }}
            </UiBadge>
          </template>
          <div class="form-stack">
            <UiSteps :steps="DISCIPLINE_STEPS" :current="STEP_OF_STATUS[discipline.status] ?? 0" />
            <div>
              <v-btn variant="outlined" @click="disciplineOpen = true">View details</v-btn>
            </div>
          </div>
        </UiCard>
      </section>

      <UiCard title="Announcements" description="Updates from your child's teachers.">
        <UiPostFeed
          :posts="posts"
          :loading="loadingChild"
          :sending="sending"
          empty-title="No announcements yet"
          empty-description="Teacher announcements will appear here."
          @comment="comment"
          :can-delete-comment="canDelete"
          @delete-comment="removeComment"
        />
      </UiCard>
    </template>

    <UiDialog
      v-model="disciplineOpen"
      :title="discipline ? `${reportType(discipline.report_type).name} concern` : 'Discipline case'"
      :description="discipline ? `Case #${discipline.id} · ${formatDateTime(discipline.createdAt)}` : ''"
      size="lg"
      :persistent="false"
    >
      <div v-if="discipline" class="form-stack">
        <UiDetailList
          :items="[
            { label: 'Grade level', value: discipline.grade_level },
            { label: 'Incident', value: [formatDate(discipline.report_date), formatTime(discipline.report_time)].filter(Boolean).join(', ') },
            { label: 'Description', value: discipline.report_description || 'No description provided.', span: 2 },
            ...(discipline.disciplinary_action_name
              ? [{ label: 'Disciplinary action', value: discipline.disciplinary_action_name, span: 2 }]
              : []),
            ...(complianceStatus(discipline.compliance)
              ? [{ label: 'Compliance', value: complianceStatus(discipline.compliance).title }]
              : []),
            ...(discipline.compliance_due_date
              ? [{ label: 'Due date', value: formatDate(discipline.compliance_due_date) }]
              : []),
            ...(discipline.comments || !discipline.prefect_notes
              ? [{ label: noteBy(discipline.adviser_notes_by_name, 'class adviser'), value: discipline.comments || 'No comments yet.', span: 2 }]
              : []),
            ...(discipline.prefect_notes
              ? [{ label: noteBy(discipline.prefect_notes_by_name, 'Prefect of Discipline'), value: discipline.prefect_notes, span: 2 }]
              : []),
            { label: 'Last updated', value: formatDateTime(discipline.updatedAt) },
          ]"
        />
        <div>
          <p class="text-body-2 text-medium-emphasis mb-2">Tagged students</p>
          <UiBadgeList :items="tagged" empty="None tagged." />
        </div>
      </div>
      <template #footer="{ close }">
        <v-btn variant="outlined" @click="close">Close</v-btn>
      </template>
    </UiDialog>
  </div>
</template>

<style scoped>
.child-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
</style>
